// AdministrativeAreaLayerOverlay — imperatively manages MapLibre administrative-area
// (e.g. Warsaw district) fill/outline/label layers. Renders nothing to the DOM;
// all work is done via the MapLibre imperative API. Mirrors the structure/lifecycle
// established by TransportLayerOverlay (REQ-6.9, administrative-area-filtering).
import * as React from 'react';
import type { MapInstance } from '@ooh/ui/components/map-view';
import type { AdminBoundaryFeature, AdminBoundaryFeatureCollection } from '@ooh/contracts';

const ADMIN_AREA_SOURCE_ID = 'administrative-area-source';
const ADMIN_AREA_LABELS_SOURCE_ID = 'administrative-area-labels-source';
const ADMIN_AREA_FILL_LAYER_ID = 'administrative-area-fill';
const ADMIN_AREA_OUTLINE_LAYER_ID = 'administrative-area-outline';
const ADMIN_AREA_LABEL_LAYER_ID = 'administrative-area-label';

/**
 * Matches `packages/ui/src/components/map-view.tsx`'s pin cluster layer id.
 * Duplicated locally (not imported) per the `transport-layers/constants.ts`
 * `PIN_CLUSTER_LAYER_ID` convention — there is no shared constant home across
 * features, and cross-feature imports are forbidden (WEB_ARCHITECTURE.md).
 */
const PIN_CLUSTER_LAYER_ID = 'clusters';

const EMPTY_FEATURE_COLLECTION: AdminBoundaryFeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

const DEFAULT_FILL_COLOR = 'rgba(99,102,241,0.06)';
const SELECTED_FILL_COLOR = 'rgba(79,70,229,0.32)';
const DEFAULT_LINE_COLOR = 'rgba(99,102,241,0.55)';
const SELECTED_LINE_COLOR = '#4338ca';

export interface AdministrativeAreaLayerOverlayProps {
  mapInstance: MapInstance | null;
  /** Bbox-scoped administrative-area geometry. `undefined` (e.g. a failed fetch) is
   * treated as an empty FeatureCollection — a geometry failure never breaks pins. */
  geoJson: AdminBoundaryFeatureCollection | undefined;
  /** Local map UI state — this component never reads or writes shared filter state. */
  isEnabled: boolean;
  /** Stable administrative-area IDs active in the shared `administrativeAreaIds` filter. */
  selectedIds: readonly string[];
}

// ─── Geometry helpers ──────────────────────────────────────────────────────────

/** Naive centroid of a Polygon/MultiPolygon's outer ring — a label anchor point, not a precise centroid. */
function computeLabelPoint(feature: AdminBoundaryFeature): [number, number] {
  const ring =
    feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates[0]
      : feature.geometry.coordinates[0]?.[0];
  if (!ring || ring.length === 0) return [0, 0];
  const [sumLon, sumLat] = ring.reduce(
    ([lon, lat], [pointLon, pointLat]) => [lon + pointLon, lat + pointLat],
    [0, 0],
  );
  return [sumLon / ring.length, sumLat / ring.length];
}

function buildLabelsGeoJson(geoJson: AdminBoundaryFeatureCollection) {
  return {
    type: 'FeatureCollection' as const,
    features: geoJson.features.map((f) => ({
      type: 'Feature' as const,
      properties: { id: f.properties.id, name: f.properties.name },
      geometry: { type: 'Point' as const, coordinates: computeLabelPoint(f) },
    })),
  };
}

// ─── Layer builders ────────────────────────────────────────────────────────────

function ensureSources(map: MapInstance, geoJson: AdminBoundaryFeatureCollection): void {
  if (!map.getSource(ADMIN_AREA_SOURCE_ID)) {
    // Cast required: MapLibre's addSource types 'geojson' data as GeoJSON.GeoJsonObject; our
    // contracts-typed AdminBoundaryFeatureCollection is not assignable without the cast.
    // promoteId: 'id' lets setFeatureState key off properties.id (no top-level feature id).
    map.addSource(ADMIN_AREA_SOURCE_ID, {
      type: 'geojson',
      data: geoJson as never,
      promoteId: 'id',
    });
  }
  if (!map.getSource(ADMIN_AREA_LABELS_SOURCE_ID)) {
    map.addSource(ADMIN_AREA_LABELS_SOURCE_ID, {
      type: 'geojson',
      data: buildLabelsGeoJson(geoJson) as never,
    });
  }
}

function ensureFillLayer(map: MapInstance, beforeId: string | undefined): void {
  if (map.getLayer(ADMIN_AREA_FILL_LAYER_ID)) return;
  map.addLayer(
    {
      id: ADMIN_AREA_FILL_LAYER_ID,
      type: 'fill',
      source: ADMIN_AREA_SOURCE_ID,
      paint: {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          SELECTED_FILL_COLOR,
          DEFAULT_FILL_COLOR,
        ],
      },
      // Cast required: MapLibre's addLayer expects a typed LayerSpecification union; our plain
      // object with a `case` expression is not assignable without the cast.
    } as never,
    beforeId,
  );
}

function ensureOutlineLayer(map: MapInstance, beforeId: string | undefined): void {
  if (map.getLayer(ADMIN_AREA_OUTLINE_LAYER_ID)) return;
  map.addLayer(
    {
      id: ADMIN_AREA_OUTLINE_LAYER_ID,
      type: 'line',
      source: ADMIN_AREA_SOURCE_ID,
      paint: {
        'line-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          SELECTED_LINE_COLOR,
          DEFAULT_LINE_COLOR,
        ],
        'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2.5, 1],
      },
      layout: { 'line-join': 'round' },
      // Cast required: same LayerSpecification union constraint as the fill layer above.
    } as never,
    beforeId,
  );
}

function ensureLabelLayer(map: MapInstance, beforeId: string | undefined): void {
  if (map.getLayer(ADMIN_AREA_LABEL_LAYER_ID)) return;
  map.addLayer(
    {
      id: ADMIN_AREA_LABEL_LAYER_ID,
      type: 'symbol',
      source: ADMIN_AREA_LABELS_SOURCE_ID,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#312e81',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.4,
      },
      // Cast required: same LayerSpecification union constraint as the fill layer above.
    } as never,
    beforeId,
  );
}

function getBeforeId(map: MapInstance): string | undefined {
  return map.getLayer(PIN_CLUSTER_LAYER_ID) ? PIN_CLUSTER_LAYER_ID : undefined;
}

function setLayersVisibility(map: MapInstance, visible: boolean): void {
  const visibility = visible ? 'visible' : 'none';
  for (const id of [
    ADMIN_AREA_FILL_LAYER_ID,
    ADMIN_AREA_OUTLINE_LAYER_ID,
    ADMIN_AREA_LABEL_LAYER_ID,
  ]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility);
  }
}

/**
 * Syncs the `selected` feature-state for every feature RETURNED in the current bbox
 * response against `selectedIds`. Only iterates over `geoJson.features` — a selected id
 * with no matching feature in the current viewport is silently skipped (never looped over
 * to call `setFeatureState` on a nonexistent feature), so a filtered area outside the
 * viewport can never throw or remove the highlight for areas that ARE present.
 */
function syncSelectionState(
  map: MapInstance,
  geoJson: AdminBoundaryFeatureCollection,
  selectedIds: ReadonlySet<string>,
): void {
  if (!map.getSource(ADMIN_AREA_SOURCE_ID)) return;
  for (const feature of geoJson.features) {
    map.setFeatureState(
      { source: ADMIN_AREA_SOURCE_ID, id: feature.properties.id },
      { selected: selectedIds.has(feature.properties.id) },
    );
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Renders the toggleable administrative-area boundary layer. Visibility is driven
 * entirely by the caller-owned `isEnabled` local UI state — this component never reads
 * or writes `administrativeAreaIds`. `selectedIds` is used ONLY for feature-state
 * highlighting; no click handler is registered on the fill/outline layers, so a polygon
 * click can never mutate the shared filter (see spec scenario "Polygon click does not
 * mutate shared filter").
 */
export function AdministrativeAreaLayerOverlay({
  mapInstance,
  geoJson,
  isEnabled,
  selectedIds,
}: AdministrativeAreaLayerOverlayProps): null {
  const resolvedGeoJson = geoJson ?? EMPTY_FEATURE_COLLECTION;
  const selectedIdSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  React.useEffect(() => {
    if (mapInstance === null) return;

    if (isEnabled) {
      ensureSources(mapInstance, resolvedGeoJson);
      const beforeId = getBeforeId(mapInstance);
      ensureFillLayer(mapInstance, beforeId);
      ensureOutlineLayer(mapInstance, beforeId);
      ensureLabelLayer(mapInstance, beforeId);

      const source = mapInstance.getSource(ADMIN_AREA_SOURCE_ID) as
        | { setData: (d: AdminBoundaryFeatureCollection) => void }
        | undefined;
      source?.setData(resolvedGeoJson);
      const labelsSource = mapInstance.getSource(ADMIN_AREA_LABELS_SOURCE_ID) as
        | { setData: (d: ReturnType<typeof buildLabelsGeoJson>) => void }
        | undefined;
      labelsSource?.setData(buildLabelsGeoJson(resolvedGeoJson));
    }

    setLayersVisibility(mapInstance, isEnabled);
  }, [mapInstance, isEnabled, resolvedGeoJson]);

  React.useEffect(() => {
    if (mapInstance === null || !isEnabled) return;
    syncSelectionState(mapInstance, resolvedGeoJson, selectedIdSet);
  }, [mapInstance, isEnabled, resolvedGeoJson, selectedIdSet]);

  return null;
}
